/**
 * resendInvite — RPC contract for the resendInvite callable.
 *
 * Cross-process contract between the web member-management seam
 * (web/src/members/members.api.ts) and
 * firebase/src/invites/resendInvite.api.ts (onCall handler). Both
 * ends parse with `.strict()` — an extra key fails the whole request.
 *
 * A resend is a create: the handler re-runs createInvite's authorization
 * spine (caller holds tickets.members.invite → target role exists →
 * role-power containment → scope tenancy → resolve-never-create) against
 * the SUPERSEDED invite's own stored email/displayName/roleId/scope — never
 * a client-supplied redeclaration of them, so a resend can't smuggle a wider
 * role or scope than the original invite carried. `token` (+ optional
 * `locale`) is the whole input.
 *
 * Fields justified:
 *   token   — the pending invite to supersede; doc id of
 *             `workspaces/{orgId}/invites/{token}`. The handler rejects a
 *             token that doesn't resolve to a `pending` invite (already
 *             redeemed/revoked/expired, or resent a moment earlier by a
 *             concurrent call — see resendInvite.api.ts's transactional
 *             re-check).
 *   locale  — the UI language ('de'/'en') the resending admin is currently
 *             viewing; the replacement invitation email renders in this
 *             language, matching CreateInviteInput's own field. The
 *             superseded invite carries no persisted locale to fall back
 *             to (Invite's own schema never stored one). Absent → 'de'.
 *
 * Output:
 *   token   — the newly minted invite's token (server-generated), doc id of
 *             the replacement `workspaces/{orgId}/invites/{token}`. The
 *             superseded invite is flipped to `status: 'revoked'` in the
 *             same atomic transaction that writes the replacement.
 *
 * Excluded:
 *   email / displayName / roleId / scope — read server-side off the invite
 *                   being superseded, never re-declared by the caller.
 *   invitedBy / status / createdAt / expiresAt — server-stamped, matching
 *                   CreateInviteInput's exclusions.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const ResendInviteInput = z
	.object({
		orgId: Id,
		token: z
			.string()
			.min(1)
			.describe(
				"the pending invite to supersede — doc id of workspaces/{orgId}/invites/{token}",
			),
		locale: LocaleEnum.nullish(),
	})
	.strict();
export type ResendInviteInput = z.infer<typeof ResendInviteInput>;

export const ResendInviteOutput = z
	.object({
		token: z
			.string()
			.min(1)
			.describe(
				"the newly minted invite token — doc id of workspaces/{orgId}/invites/{token}",
			),
	})
	.strict();
export type ResendInviteOutput = z.infer<typeof ResendInviteOutput>;
